// Pure geometry + layout logic for the Zaunplaner (all lengths in mm)

export const GATE_DEFAULTS = {
  gehtuere: { label: "Gehtüre", short: "GT", width: 1350 },
  doppelflugel: { label: "Doppelflügeltor", short: "DFT", width: 3350 },
  schiebetor: { label: "Freitragendes Schiebetor", short: "ST", width: 4250 },
};

// Modelle ohne Pfosten (selbsttragend, Bodenprofil): nur Torpfosten, nur bestimmte Profile
export const POSTLESS_MODELS = ["INFINITY"];
export const POSTLESS_GATE_PROFILES = ["100x100", "120x120", "150x150", "200x100"];
export const isPostless = (plan) => POSTLESS_MODELS.includes(plan?.model);
export const allowedPostProfiles = (plan, profiles) => (isPostless(plan) ? profiles.filter((p) => POSTLESS_GATE_PROFILES.some((a) => String(p).replace(/\s|mm/g, "").startsWith(a))) : profiles);

export const SEG_LABEL = (i) => String.fromCharCode(65 + (i % 26)) + (i >= 26 ? Math.floor(i / 26) : "");

// Torpfosten: diese Profile nicht anbieten (Kundenwunsch)
export const GATE_POST_EXCLUDE = ["80x50", "75x75", "100x100"];
const normProfil = (p) => String(p || "").replace(/\s|mm/gi, "");
export const gatePostProfiles = (profiles) => profiles.filter((p) => !GATE_POST_EXCLUDE.includes(normProfil(p)));

// Farben für mehrere Abschnitte im Planer
export const SECTION_COLORS = ["#18181b", "#2563eb", "#059669", "#d97706", "#db2777", "#7c3aed", "#0891b2", "#ca8a04"];

export function profileDims(profil) {
  return (String(profil || "").match(/\d+/g) || []).slice(0, 2).map(Number);
}

export function emptyPlan(pricelist) {
  const profiles = (pricelist?.accessories?.pfosten?.profilTypes || []).map((p) => p.profil);
  const pick = (pref) => profiles.find((p) => p.startsWith(pref)) || profiles[0] || "";
  const gateProfiles = gatePostProfiles(profiles);
  const gatePick = gateProfiles.find((p) => normProfil(p) === "100x100x4") || gateProfiles.find((p) => p.startsWith("120x120")) || gateProfiles[0] || profiles[0] || "";
  const models = Object.keys(pricelist?.products || {}).filter((k) => pricelist.products[k].zaunelement?.length);
  const model = models[0] || "";
  const variants = pricelist?.products?.[model]?.zaunelementVariants || [];
  return {
    model,
    variant: variants.length ? variants[0].name : null,
    height: 1500,
    postHeight: 1560,
    maxField: 2000,
    posts: {
      field: { profil: pick("80x50"), dim: profileDims(pick("80x50"))[0] || 80 },
      corner: { profil: pick("100x100mm"), dim: profileDims(pick("100x100mm"))[0] || 100 },
      gate: { profil: gatePick, dim: profileDims(gatePick)[0] || 120 },
    },
    points: [],
    closed: false,
    gates: [], // {id, seg, type, width, offset}
    view: "grundriss",
    ansicht: { items: [], wandStart: false, wandEnd: false },
  };
}

// Ein Abschnitt (Section) ist ein vollständiger Einzel-Plan mit eigener Linie/Toren/Einstellungen
export function newSection(pricelist, index = 0, overrides = {}) {
  return {
    id: crypto.randomUUID(),
    name: `Abschnitt ${index + 1}`,
    color: SECTION_COLORS[index % SECTION_COLORS.length],
    ...emptyPlan(pricelist),
    ...overrides,
  };
}

// Container-Plan (mehrere Abschnitte). Migriert alte Einzel-Pläne.
export function emptyProject(pricelist) {
  return { sections: [newSection(pricelist, 0)], active: 0, view: "grundriss" };
}

export function migrateProject(obj, pricelist) {
  if (!obj) return emptyProject(pricelist);
  if (Array.isArray(obj.sections) && obj.sections.length) {
    const sections = obj.sections.map((s, i) => normalizeSection(s, pricelist, i));
    return { sections, active: Math.min(obj.active || 0, sections.length - 1), view: obj.view || "grundriss" };
  }
  // Legacy: Einzel-Plan -> ein Abschnitt
  const sec = normalizeSection({ ...emptyPlan(pricelist), ...obj }, pricelist, 0);
  return { sections: [sec], active: 0, view: obj.view || sec.view || "grundriss" };
}

function normalizeSection(s, pricelist, i) {
  const base = emptyPlan(pricelist);
  const sec = { id: s.id || crypto.randomUUID(), name: s.name || `Abschnitt ${i + 1}`, color: s.color || SECTION_COLORS[i % SECTION_COLORS.length], ...base, ...s };
  // ausgeschlossene Torpfosten-Profile zurücksetzen
  const profiles = (pricelist?.accessories?.pfosten?.profilTypes || []).map((p) => p.profil);
  if (!gatePostProfiles(profiles).includes(sec.posts?.gate?.profil)) {
    sec.posts = { ...base.posts, ...(sec.posts || {}), gate: base.posts.gate };
  }
  return sec;
}

export function segmentsOf(plan) {
  const pts = plan.points;
  const n = pts.length;
  const count = plan.closed && n >= 3 ? n : Math.max(0, n - 1);
  const segs = [];
  for (let i = 0; i < count; i++) {
    const a = pts[i], b = pts[(i + 1) % n];
    const dx = b.x - a.x, dy = b.y - a.y;
    const length = Math.round(Math.hypot(dx, dy));
    segs.push({ idx: i, a, b, length, dir: length ? { x: dx / length, y: dy / length } : { x: 1, y: 0 }, isClosing: plan.closed && i === n - 1 });
  }
  return segs;
}

export function setSegmentLength(plan, idx, newLen) {
  const segs = segmentsOf(plan);
  const s = segs[idx];
  if (!s || s.isClosing || newLen <= 0) return plan;
  const d = newLen - s.length;
  const points = plan.points.map((p, j) => (j > idx ? { x: Math.round(p.x + s.dir.x * d), y: Math.round(p.y + s.dir.y * d) } : p));
  return { ...plan, points };
}

function fieldsForRun(A, pf, maxField) {
  if (A <= 0) return [];
  let n = 1;
  while (n < 5000 && (A - (n - 1) * pf) / n >= maxField) n++;
  const fw = Math.round((A - (n - 1) * pf) / n);
  return Array.from({ length: n }, () => fw);
}

function clampOffset(gate, L, gd) {
  return Math.max(0, Math.min(Number(gate.offset) || 0, Math.max(0, L - gate.width - gd)));
}

function layoutSegment(plan, s, startKind, endKind, warnings) {
  const L = s.length;
  const label = plan.segLabels?.[s.idx] || SEG_LABEL(s.idx);
  const pf = (plan.segDims && plan.segDims[s.idx]) || plan.posts.field.dim, gd = plan.posts.gate.dim;
  const dimOf = (k) => plan.posts[k].dim;
  const els = [];
  let startIsGate = startKind === "gate", endIsGate = endKind === "gate";
  els.push({ kind: "post", profile: startKind, from: 0, to: dimOf(startKind), vertex: true });
  const pushFields = (run) => {
    fieldsForRun(run, pf, plan.maxField).forEach((fw, i, arr) => {
      const from = els[els.length - 1].to;
      els.push({ kind: "field", width: fw, from, to: from + fw });
      if (i < arr.length - 1) els.push({ kind: "post", profile: "field", from: from + fw, to: from + fw + pf });
    });
  };
  const gates = plan.gates.filter((g) => g.seg === s.idx).map((g) => ({ ...g, offset: clampOffset(g, L, gd) })).sort((a, b) => a.offset - b.offset);
  for (const g of gates) {
    const last = els[els.length - 1];
    const run = g.offset - gd - last.to;
    let off;
    if (run < 100) {
      // gate abuts previous post -> that post becomes a gate post
      last.profile = "gate";
      last.to = last.from + gd;
      if (last.vertex) startIsGate = startIsGate || els.indexOf(last) === 0;
      off = last.to;
    } else {
      pushFields(run);
      const from = els[els.length - 1].to;
      els.push({ kind: "post", profile: "gate", from, to: from + gd });
      off = from + gd;
    }
    if (off + g.width + gd > L + 1) warnings.push(`Segment ${label}: Tor (${g.width} mm) passt nicht mehr in das Segment.`);
    els.push({ kind: "gate", id: g.id, type: g.type, width: g.width, from: off, to: off + g.width });
    els.push({ kind: "post", profile: "gate", from: off + g.width, to: off + g.width + gd });
  }
  const last = els[els.length - 1];
  const rest = L - dimOf(endKind) - last.to;
  if (rest < 100 && gates.length) {
    last.vertex = true; last.from = L - gd; last.to = L; endIsGate = true;
  } else {
    if (rest < 100) warnings.push(`Segment ${label}: Segment zu kurz (${L} mm).`);
    pushFields(Math.max(0, rest));
    els.push({ kind: "post", profile: endKind, from: L - dimOf(endKind), to: L, vertex: true });
  }
  const fields = els.filter((e) => e.kind === "field");
  if (fields.some((f) => f.width < 300)) warnings.push(`Segment ${label}: sehr schmales Feld (< 300 mm) – Maße prüfen.`);
  return { ...s, label, elements: els, fields, gates: els.filter((e) => e.kind === "gate"),
    innerPosts: els.filter((e) => e.kind === "post" && !e.vertex), startIsGate, endIsGate };
}

export function computeLayout(plan0) {
  const postless = isPostless(plan0);
  const plan = postless ? { ...plan0, maxField: Infinity, posts: { ...plan0.posts, field: { profil: "", dim: 0 }, corner: { profil: "", dim: 0 } } } : plan0;
  const segs = segmentsOf(plan);
  const n = plan.points.length;
  const isOpen = !(plan.closed && n >= 3);
  let vProf = Array.from({ length: n }, () => "corner");
  let outSegs = [], warnings = [];
  for (let pass = 0; pass < 2; pass++) {
    warnings = [];
    outSegs = segs.map((s) => layoutSegment(plan, s, vProf[s.idx], vProf[(s.idx + 1) % n], warnings));
    const next = [...vProf];
    for (const s of outSegs) {
      if (s.startIsGate) next[s.idx] = "gate";
      if (s.endIsGate) next[(s.idx + 1) % n] = "gate";
    }
    if (next.join() === vProf.join()) break;
    vProf = next;
  }

  const vertexList = [];
  const vertexPosts = {};
  if (segs.length) {
    for (let v = 0; v < n; v++) {
      const kind = vProf[v];
      const role = isOpen && (v === 0 || v === n - 1) ? "end" : "corner";
      if (!postless || kind === "gate") vertexPosts[kind] = (vertexPosts[kind] || 0) + 1;
      vertexList.push({ v, kind, role });
    }
  }

  const fieldMap = {}, postMap = {}, gateList = [];
  const addPost = (kind, c = 1) => { const p = plan.posts[kind].profil; if (!p) return; postMap[p] = (postMap[p] || 0) + c; };
  if (postless) for (const s of outSegs) { s.elements = s.elements.filter((e) => e.kind !== "post" || e.profile === "gate"); s.innerPosts = s.innerPosts.filter((e) => e.profile === "gate"); }
  for (const s of outSegs) {
    for (const f of s.fields) fieldMap[f.width] = (fieldMap[f.width] || 0) + 1;
    for (const p of s.innerPosts) addPost(p.profile);
    for (const g of s.gates) gateList.push({ type: g.type, width: g.width, seg: s.label });
  }
  for (const [kind, c] of Object.entries(vertexPosts)) addPost(kind, c);
  const fields = Object.entries(fieldMap).map(([w, c]) => ({ width: Number(w), count: c })).sort((a, b) => b.width - a.width);
  const posts = Object.entries(postMap).map(([profil, count]) => ({ profil, count }));
  return {
    segments: outSegs, vertices: vertexList, fields, posts, gates: gateList, warnings,
    totalLength: outSegs.reduce((a, s) => a + s.length, 0),
    fieldCount: fields.reduce((a, f) => a + f.count, 0),
    postCount: posts.reduce((a, p) => a + p.count, 0),
  };
}

export function pointAlong(seg, t) {
  return { x: seg.a.x + seg.dir.x * t, y: seg.a.y + seg.dir.y * t };
}

export function bounds(points) {
  if (!points.length) return { minX: -2000, minY: -2000, maxX: 12000, maxY: 8000 };
  const xs = points.map((p) => p.x), ys = points.map((p) => p.y);
  return { minX: Math.min(...xs), minY: Math.min(...ys), maxX: Math.max(...xs), maxY: Math.max(...ys) };
}

// ---- angles & segment removal ----
const DEG = 180 / Math.PI;

function signedTurn(prev, cur) {
  const cross = prev.dir.x * cur.dir.y - prev.dir.y * cur.dir.x;
  const dot = prev.dir.x * cur.dir.x + prev.dir.y * cur.dir.y;
  return Math.atan2(cross, dot) * DEG;
}

// Angle at the start vertex of segment i (between previous segment and segment i), 0..180
export function vertexAngle(plan, i) {
  const segs = segmentsOf(plan);
  const prev = i > 0 ? segs[i - 1] : plan.closed && segs.length >= 3 ? segs[segs.length - 1] : null;
  const cur = segs[i];
  if (!prev || !cur) return null;
  const t = signedTurn(prev, cur);
  return { angle: Math.round((180 - Math.abs(t)) * 10) / 10, turn: t };
}

function rotateFrom(plan, i, deltaDeg) {
  const c = plan.points[i];
  const th = deltaDeg / DEG, cos = Math.cos(th), sin = Math.sin(th);
  const points = plan.points.map((p, j) => {
    if (j <= i) return p;
    const dx = p.x - c.x, dy = p.y - c.y;
    return { x: c.x + dx * cos - dy * sin, y: c.y + dx * sin + dy * cos };
  });
  return { ...plan, points };
}

export function setVertexAngle(plan, i, angleDeg) {
  const va = vertexAngle(plan, i);
  if (!va || !(angleDeg > 0 && angleDeg < 180)) return plan;
  const sign = va.turn < 0 ? -1 : 1;
  const target = sign * (180 - angleDeg);
  return rotateFrom(plan, i, target - va.turn);
}

export function flipVertex(plan, i) {
  const va = vertexAngle(plan, i);
  if (!va) return plan;
  return rotateFrom(plan, i, -2 * va.turn);
}

export function removeSegment(plan, i) {
  const segs = segmentsOf(plan);
  const s = segs[i];
  if (!s) return plan;
  if (s.isClosing) return { ...plan, closed: false, gates: plan.gates.filter((g) => g.seg !== i) };
  const p0 = plan.points[i], p1 = plan.points[i + 1];
  const shift = { x: p0.x - p1.x, y: p0.y - p1.y };
  const points = plan.points.filter((_, j) => j !== i + 1).map((p, j) => (j > i ? { x: p.x + shift.x, y: p.y + shift.y } : p));
  const gates = plan.gates.filter((g) => g.seg !== i).map((g) => (g.seg > i ? { ...g, seg: g.seg - 1 } : g));
  return { ...plan, points, gates, closed: plan.closed && points.length >= 3 };
}


// ---- Werkzeichnung: Beton-Außenkante -> Zaunlinie (Offset) ----
export const LUNAR_LAMELLA = { height: 40, gap: 20, bottom: 60, thickness: 20 };
export const GATE_BOTTOM = { gehtuere: 70, doppelflugel: 70, schiebetor: 110 }; // Bodenluft Tore
export const DEFAULT_WERK = { enabled: false, concreteWidth: 1430, inset: 80, side: 1, houseStart: null, houseEnd: null, lamella: { ...LUNAR_LAMELLA } };

export function lamellaHeight(n, lam = LUNAR_LAMELLA) {
  return n * lam.height + (n - 1) * lam.gap;
}
export function lamellaCount(height, lam = LUNAR_LAMELLA) {
  return Math.max(1, Math.round((height + lam.gap) / (lam.height + lam.gap)));
}

function offsetPolyline(points, closed, d) {
  const n = points.length;
  const count = closed ? n : n - 1;
  const lines = [];
  for (let i = 0; i < count; i++) {
    const a = points[i], b = points[(i + 1) % n];
    const L = Math.hypot(b.x - a.x, b.y - a.y) || 1;
    const nx = -(b.y - a.y) / L, ny = (b.x - a.x) / L;
    lines.push({ a: { x: a.x + nx * d, y: a.y + ny * d }, b: { x: b.x + nx * d, y: b.y + ny * d }, dir: { x: (b.x - a.x) / L, y: (b.y - a.y) / L } });
  }
  const inter = (l1, l2) => {
    const den = l1.dir.x * l2.dir.y - l1.dir.y * l2.dir.x;
    if (Math.abs(den) < 1e-9) return l2.a;
    const t = ((l2.a.x - l1.a.x) * l2.dir.y - (l2.a.y - l1.a.y) * l2.dir.x) / den;
    return { x: l1.a.x + l1.dir.x * t, y: l1.a.y + l1.dir.y * t };
  };
  const out = [];
  for (let i = 0; i < n; i++) {
    if (closed) out.push(inter(lines[(i - 1 + count) % count], lines[i % count]));
    else if (i === 0) out.push(lines[0].a);
    else if (i === n - 1) out.push(lines[count - 1].b);
    else out.push(inter(lines[i - 1], lines[i]));
  }
  return out;
}

function autoSide(points, closed) {
  let sum = 0;
  const n = points.length;
  for (let i = 0; i < (closed ? n : n - 1); i++) {
    const a = points[i], b = points[(i + 1) % n];
    sum += a.x * b.y - b.x * a.y;
  }
  if (!closed) {
    let turns = 0;
    for (let i = 1; i < n - 1; i++) {
      const d1 = { x: points[i].x - points[i - 1].x, y: points[i].y - points[i - 1].y };
      const d2 = { x: points[i + 1].x - points[i].x, y: points[i + 1].y - points[i].y };
      turns += d1.x * d2.y - d1.y * d2.x;
    }
    return turns >= 0 ? 1 : -1;
  }
  return sum >= 0 ? 1 : -1;
}

// Returns { plan: derived fence-line plan, concrete: polygon points, side }
export function deriveWerk(plan) {
  const w = plan.werk;
  const n = plan.points.length;
  if (!w?.enabled || n < 2) return { plan, concrete: null };
  const closed = plan.closed && n >= 3;
  const side = autoSide(plan.points, closed) * (w.side || 1);
  const inset = Number(w.inset) || 0;
  let fence = offsetPolyline(plan.points, closed, side * inset);
  if (!closed) {
    const move = (p, q, dist) => { const L = Math.hypot(q.x - p.x, q.y - p.y) || 1; return { x: p.x + (q.x - p.x) / L * dist, y: p.y + (q.y - p.y) / L * dist }; };
    const startRed = w.houseStart != null && w.houseStart !== "" ? Number(w.houseStart) : inset;
    const endRed = w.houseEnd != null && w.houseEnd !== "" ? Number(w.houseEnd) : inset;
    fence[0] = move(fence[0], fence[1], startRed);
    fence[n - 1] = move(fence[n - 1], fence[n - 2], endRed);
  }
  const inner = offsetPolyline(plan.points, closed, side * (Number(w.concreteWidth) || 0));
  const concrete = closed ? { outer: plan.points, inner } : { outer: plan.points, inner };
  return { plan: { ...plan, points: fence.map((p) => ({ x: Math.round(p.x), y: Math.round(p.y) })) }, concrete, side };
}

// ---- Ansicht (Mauerpfeiler / Betonsäulen): Elevation aus Pfeilern und Feldern ----
export const DEFAULT_ANSICHT = { items: [], wandStart: false, wandEnd: false };

const lastOf = (items, kind) => [...items].reverse().find((i) => i.kind === kind);

export function newAnsichtItem(kind, plan, opts = {}) {
  const items = plan.ansicht?.items || [];
  const id = crypto.randomUUID();
  const lastFeld = lastOf(items, "feld"), lastPf = lastOf(items, "pfeiler");
  if (kind === "pfeiler") return { id, kind, width: lastPf?.width ?? 400, height: lastPf?.height ?? Number(plan.height) + (lastFeld?.sockel || 0) + 100 };
  if (kind === "alupost") return { id, kind, profil: plan.posts.corner.profil, dim: plan.posts.corner.dim, height: null, onSockel: false };
  const gate = opts.gate || null;
  return { id, kind: "feld", width: gate ? GATE_DEFAULTS[gate].width : lastFeld?.width ?? 1800, sockel: gate ? 0 : lastFeld?.sockel ?? 0, alu: lastFeld?.alu ?? false, gate,
    gateHeight: gate ? Number(plan.height) + (lastFeld?.sockel || 0) : null };
}

export function quickAnsicht({ count, feld, pfeiler, pfeilerHeight, sockel, alu }) {
  const items = [];
  for (let i = 0; i < count; i++) {
    items.push({ id: crypto.randomUUID(), kind: "pfeiler", width: pfeiler, height: pfeilerHeight });
    items.push({ id: crypto.randomUUID(), kind: "feld", width: feld, sockel, alu: !!alu, gate: null });
  }
  items.push({ id: crypto.randomUUID(), kind: "pfeiler", width: pfeiler, height: pfeilerHeight });
  return items;
}

export function ansichtLayout(plan) {
  const a = { ...DEFAULT_ANSICHT, ...(plan.ansicht || {}) };
  const pf = plan.posts.field.dim, gd = plan.posts.gate.dim;
  const els = [], warnings = [], fieldMap = {}, postMap = {}, gateList = [];
  const addPost = (kind, c) => { const p = plan.posts[kind].profil; postMap[p] = (postMap[p] || 0) + c; };
  let x = 0, feldNo = 0, pfNo = 0, zfNo = 0;
  const postless = isPostless(plan);
  const postH = Number(plan.postHeight) || Number(plan.height) + 60;
  const lamBottom = Number(plan.werk?.lamella?.bottom ?? LUNAR_LAMELLA.bottom);
  const capExtra = postH - Number(plan.height) - lamBottom; // Pfosten über Zaunoberkante
  const vis = a.items.filter((i) => !(i.kind === "pfeiler" && i.disabled));
  const isFence = (i) => i && i.kind === "feld" && !i.gate;
  const aluDim = (it) => Number(it.dim) || plan.posts[it.profile]?.dim || profileDims(it.profil || plan.posts[it.profile]?.profil || plan.posts.corner.profil)[0] || 0;
  // "auf Sockel": Pfosten steht auf dem Sockel des Nachbarfeldes (kein eigener Sockel, keine zusätzliche Länge) und verkürzt dessen Element
  const leadsInto = (j) => vis[j]?.kind === "alupost" && vis[j].onSockel && isFence(vis[j + 1]);
  const trailsFrom = (j) => vis[j]?.kind === "alupost" && vis[j].onSockel && !isFence(vis[j + 1]) && isFence(vis[j - 1]);
  const gateOnSockel = (j) => vis[j]?.kind === "feld" && vis[j].gate && vis[j].alu && vis[j].postsOnSockel;
  const leadIn = (j) => (leadsInto(j - 1) ? aluDim(vis[j - 1]) : gateOnSockel(j - 1) && isFence(vis[j]) ? gd : 0);
  const trailIn = (j) => (trailsFrom(j + 1) ? aluDim(vis[j + 1]) : gateOnSockel(j + 1) && isFence(vis[j]) ? gd : 0);
  const sockelOf = (i) => (isFence(i) ? Number(i.sockel) || 0 : 0);
  if (a.wandStart) els.push({ kind: "wand", from: 0, to: 0 });
  vis.forEach((it, j) => {
    if (it.kind === "pfeiler") {
      pfNo = a.items.filter((i) => i.kind === "pfeiler").indexOf(it) + 1;
      const w = Number(it.width) || 0;
      els.push({ kind: "pfeiler", id: it.id, no: pfNo, from: x, to: x + w, width: w, height: Number(it.height) || 0 });
      x += w;
      return;
    }
    if (it.kind === "alupost") {
      const profil = it.profil || plan.posts[it.profile]?.profil || plan.posts.corner.profil;
      const d = aluDim(it);
      const lead = leadsInto(j), trail = trailsFrom(j);
      const ns = lead ? sockelOf(vis[j + 1]) : trail ? sockelOf(vis[j - 1]) : sockelOf(vis[j + 1]) || sockelOf(vis[j - 1]);
      const onS = lead || trail;
      const ph = Number(it.height) || (onS ? postH : postH + ns);
      const from = trail ? x - d : x;
      els.push({ kind: "post", id: it.id, profile: "corner", profil, from, to: from + d, sockel: onS ? ns : 0, height: ph, standalone: true, autoHeight: !Number(it.height) });
      const key = `${profil}|${ph}`;
      postMap[key] = (postMap[key] || 0) + 1;
      if (!onS) x += d;
      return;
    }
    feldNo += 1;
    const L = Number(it.width) || 0, sockel = Number(it.sockel) || 0, isGate = !!it.gate;
    const kind = isGate ? "gate" : "field";
    const useAlu = !!it.alu && (isGate || !postless);
    const gateOnS = isGate && useAlu && !!it.postsOnSockel;
    // zwei Alu-Tore nebeneinander: gemeinsamer Torpfosten (linker Pfosten entfällt)
    const sharedL = isGate && useAlu && !!vis[j - 1] && vis[j - 1].kind === "feld" && !!vis[j - 1].gate && !!vis[j - 1].alu;
    const gateH = isGate ? Number(it.gateHeight) || Number(plan.height) : undefined;
    const fh = isGate ? undefined : Number(it.fieldHeight) || Number(plan.height);
    const addField = (w) => { const k = `${w}|${fh}`; fieldMap[k] = (fieldMap[k] || 0) + 1; };
    if (sockel) els.push({ kind: "sockel", id: it.id, from: x, to: x + L, height: sockel });
    // Torpfosten: normal innerhalb der Lichte; "auf Sockel" außerhalb auf dem Sockel des Nachbarfeldes (Lichte bleibt voll)
    const leftOut = gateOnS && isFence(vis[j - 1]), rightOut = gateOnS && isFence(vis[j + 1]);
    const li = isGate ? 0 : leadIn(j), ti = isGate ? 0 : trailIn(j);
    const dL = useAlu ? (isGate ? (leftOut || sharedL ? 0 : gd) : (li ? 0 : pf)) : 0; // Nachbarpfosten (Tor/Alu auf Sockel) ersetzt eigenen Endpfosten
    const dR = useAlu ? (isGate ? (rightOut ? 0 : gd) : (ti ? 0 : pf)) : 0;
    const inner = L - dL - dR - li - ti;
    const gateBottom = isGate ? (GATE_BOTTOM[it.gate] ?? lamBottom) : lamBottom;
    if (useAlu) {
      const gph = (s) => Math.max(gateBottom + gateH + capExtra - s, 200);
      const mk = (from, s) => ({ kind: "post", id: it.id, profile: kind, from, to: from + (isGate ? gd : pf), sockel: s, ...(isGate ? { height: gph(s) } : {}) });
      const sl = leftOut ? sockelOf(vis[j - 1]) : isGate ? 0 : sockel, sr = rightOut ? sockelOf(vis[j + 1]) : isGate ? 0 : sockel;
      let n = 0;
      if ((isGate && !sharedL) || (!isGate && !li)) { els.push(mk(leftOut ? x - gd : x, sl)); n++; }
      if (isGate || !ti) { els.push(mk(rightOut ? x + L : x + L - (isGate ? gd : pf), sr)); n++; }
      if (isGate) { for (const s of (sharedL ? [sr] : [sl, sr])) { const key = `${plan.posts.gate.profil}|${gph(s)}`; postMap[key] = (postMap[key] || 0) + 1; } } else if (n) addPost(kind, n);
    }
    if (inner < 300) warnings.push(`Feld ${feldNo}: Lichte zu klein (${inner} mm).`);
    const split = !isGate && !postless && it.split !== false && inner >= plan.maxField;
    const x0 = x + dL + li;
    if (split) {
      const widths = fieldsForRun(inner, pf, plan.maxField);
      let fx = x0;
      widths.forEach((fw, i) => {
        zfNo += 1;
        els.push({ kind: "field", id: it.id, no: zfNo, sub: i + 1, width: fw, from: fx, to: fx + fw, sockel, height: fh, span: i === 0 ? [x, x + L] : undefined });
        addField(fw);
        fx += fw;
        if (i < widths.length - 1) { els.push({ kind: "post", id: it.id, profile: "field", from: fx, to: fx + pf, sockel, inner: true }); fx += pf; }
      });
      addPost("field", widths.length - 1);
    } else {
      zfNo += isGate ? 0 : 1;
      els.push({ kind, id: it.id, no: isGate ? feldNo : zfNo, type: it.gate || undefined, width: inner, from: x0, to: x0 + inner, sockel, span: [x, x + L], height: isGate ? gateH : fh, ...(isGate ? { bottom: gateBottom } : {}) });
      if (isGate) gateList.push({ type: it.gate, width: inner, height: gateH, seg: `Feld ${feldNo}` });
      else { addField(inner); if (!postless && inner > plan.maxField) warnings.push(`Feld ${feldNo}: ${inner} mm über Max. Feld (${plan.maxField} mm) – Teilung aktivieren.`); }
    }
    x += L;
  });
  if (a.wandEnd) els.push({ kind: "wand", from: x, to: x });
  const fields = Object.entries(fieldMap).map(([k, c]) => { const [w, h] = k.split("|"); return { width: Number(w), count: c, height: Number(h) || Number(plan.height) }; }).sort((p, q) => q.width - p.width);
  const posts = Object.entries(postMap).map(([k, count]) => { const [profil, h] = k.split("|"); return h ? { profil, count, height: Number(h) } : { profil, count }; });
  const segments = a.items.length ? [{ idx: 0, label: "A", a: { x: 0, y: 0 }, b: { x, y: 0 }, length: x, elements: els,
    fields: els.filter((e) => e.kind === "field"), gates: els.filter((e) => e.kind === "gate"), innerPosts: els.filter((e) => e.kind === "post") }] : [];
  return {
    view: "ansicht", segments, vertices: [], fields, posts, gates: gateList, warnings, totalLength: x,
    fieldCount: fields.reduce((s, f) => s + f.count, 0), postCount: posts.reduce((s, p) => s + p.count, 0),
    pfeilerCount: a.items.filter((i) => i.kind === "pfeiler" && !i.disabled).length,
  };
}
